import { amplitudeDeviceId } from "@hamlak/shared";
import { platform } from "@/platform";
import type { CommonProps } from "@/platform";
import type { EventMap, EventName } from "./events";
import { resolveAcquisition, storeAcquisition, storedAcquisition, takeFirstLaunch } from "./launch";

export type { EventMap, EventName, StoreSource } from "./events";

/**
 * analytics 진입점. 호출부는 `analytics.track(...)`만 보고 SDK를 import하지 않는다(SDK는 platform.analytics 어댑터).
 * 프로젝트 하나에 dev·prod 를 함께 보내고 공통 프로퍼티 env 로 나눈다(AC0-182). device_id 는 amplitudeDeviceId 로 dev 만 접두.
 */
const stage = (): CommonProps["env"] => (import.meta.env.VITE_STAGE === "prod" ? "prod" : "dev");

const state = {
  appVersion: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0",
  bundleVersion: null as string | null,
};

/** 모든 이벤트·API 요청 헤더(X-App-Version·X-Bundle-Version)가 같은 값을 쓴다 */
export const commonProps = (): CommonProps => ({
  env: stage(),
  platform: platform.clientKind,
  app_version: state.appVersion,
  bundle_version: state.bundleVersion ?? state.appVersion,
});

let initOnce: Promise<void> | null = null;
/** init 진행 중 들어온 포트 호출. null = init 전이거나 끝남 → 바로 보낸다 */
let pending: Array<() => void> | null = null;

/** init 이 진행 중이면 끝난 뒤 순서대로 포트에 보낸다(오프라인 콜드 스타트에서 setUserId·이벤트 유실 방지) */
const whenReady = (fn: () => void) => {
  if (pending) pending.push(fn);
  else fn();
};
let launchChain: Promise<void> = Promise.resolve();
let unsubscribeSession: (() => void) | null = null;

/** launch_app = SDK 새 세션마다(콜드 스타트·5분 넘은 복귀). 결제창·로그인 창 복귀는 같은 세션이라 보내지 않는다. 순서 보장을 위해 직렬화. */
const onSessionStart = () => {
  launchChain = launchChain.then(async () => {
    const isFirst = takeFirstLaunch();
    let acq = storedAcquisition();
    if (!acq) {
      // Android 리퍼러 조회가 일시 실패(throw)하면 이번 세션은 organic 으로 보내고 저장하지 않는다 → 다음 세션에 다시 조회
      let referrer: string | null = null;
      let settled = true;
      if (platform.clientKind === "android") referrer = await platform.analytics.getInstallReferrer().catch(() => ((settled = false), null));
      acq = resolveAcquisition(platform.clientKind, { search: typeof location !== "undefined" ? location.search : "", referrer });
      if (settled) {
        storeAcquisition(acq);
        analytics.setUserProperties({ setOnce: { acq_source: acq.source, acq_campaign: acq.campaign } });
      }
    }
    if (isFirst) analytics.setUserProperties({ set: { env: stage() } });
    analytics.track("launch_app", { is_first_launch: isFirst, utm_source: acq.source, utm_campaign: acq.campaign });
  });
};

export const analytics = {
  /** 부트스트랩에서 1회. 키가 없으면 포트를 시작하지 않는다(track 은 포트에서 no-op). */
  init(): Promise<void> {
    if (initOnce) return initOnce;
    pending = [];
    initOnce = (async () => {
      try {
        state.appVersion = (await platform.app.getInfo()).version || state.appVersion;
        const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
        if (!apiKey) return;
        // 포트 init 전에 구독: 세션 시작 신호는 init 중에 온다(앱은 플러그인이 구독 전 신호를 보관한다)
        unsubscribeSession = platform.analytics.onSessionStart(onSessionStart);
        const deviceId = amplitudeDeviceId(stage(), await platform.device.getDeviceId());
        await platform.analytics.init({ apiKey, deviceId, commonProps: commonProps() });
      } catch {
        // SDK 초기화 실패는 기능에 영향 없음(no-op로 남는다).
      } finally {
        const queued = pending ?? [];
        pending = null;
        for (const fn of queued) fn();
      }
    })();
    return initOnce;
  },
  track<E extends EventName>(event: E, props: EventMap[E]): void {
    const payload = { ...commonProps(), ...props };
    whenReady(() => platform.analytics.track(event, payload));
  },
  setUserId(userId: string | null): void {
    whenReady(() => platform.analytics.setUserId(userId));
  },
  setUserProperties(o: { set?: Record<string, unknown>; setOnce?: Record<string, unknown> }): void {
    whenReady(() => platform.analytics.setUserProperties(o));
  },
  /** OTA 번들 버전(내장 번들이면 호출하지 않는다 → app_version) */
  setBundleVersion(version: string): void {
    state.bundleVersion = version;
    platform.analytics.setCommonProps(commonProps());
  },
  flush(): Promise<void> {
    return platform.analytics.flush();
  },
};

/** 테스트 전용: 모듈 상태 초기화(setup.ts afterEach) */
export function resetAnalyticsForTest(): void {
  initOnce = null;
  pending = null;
  launchChain = Promise.resolve();
  unsubscribeSession?.();
  unsubscribeSession = null;
  state.appVersion = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0";
  state.bundleVersion = null;
}
